import { useEffect, useState } from 'react';
import { CustomModal } from '../CustomModal';
import { Btn, InputField, SearchSuggest, Select } from '../ui';
import type { SelectOption } from '../ui';
import { displayUnitOfMeasure } from '../../utils/unitOfMeasureUtils';
import { useUserContext } from '../../contexts/UserContext';
import { useCompanyUnitsOfMeasure } from '../../hooks/useCompanyUnitsOfMeasure';
import { UNITS_OF_MEASURE } from '../../utils/constants';
import '../../css/PMEquipmentConfiguration.css';

interface AddServiceLevelModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (data: { levelName: string; targetUM: number; unit: string }) => void;
    defaultUnit: string;
    existingLevels: { levelIdentifier: string; intervalValue: number }[];
    knownLevelNames: string[];
}

export const AddServiceLevelModal = ({
    isOpen,
    onClose,
    onAdd,
    defaultUnit,
    existingLevels,
    knownLevelNames,
}: AddServiceLevelModalProps) => {
    const [levelName, setLevelName] = useState('');
    const [targetUM, setTargetUM] = useState('');
    const [unit, setUnit] = useState(defaultUnit);
    const [error, setError] = useState('');

    const { company, companyOwner } = useUserContext();
    const companyId = (company?.id as string) || companyOwner?.companyId || '';
    const { catalogOptions } = useCompanyUnitsOfMeasure(companyId);
    const unitOptions: SelectOption[] =
        catalogOptions.length > 0
            ? catalogOptions.map((opt) => ({ value: opt.value, label: opt.label }))
            : UNITS_OF_MEASURE.map((opt) => ({ value: opt.value, label: opt.label }));

    useEffect(() => {
        if (!isOpen) return;
        setLevelName('');
        setTargetUM('');
        setUnit(defaultUnit);
        setError('');
    }, [isOpen, defaultUnit]);

    const handleClose = () => {
        setLevelName('');
        setTargetUM('');
        setUnit(defaultUnit);
        setError('');
        onClose();
    };

    const handleAdd = () => {
        setError('');
        if (!levelName.trim()) {
            setError('Level name is required.');
            return;
        }
        const numericUM = parseFloat(targetUM);
        if (!targetUM || isNaN(numericUM) || numericUM <= 0) {
            setError('Target UM must be a positive number.');
            return;
        }
        if (existingLevels.some((l) => l.levelIdentifier.toLowerCase() === levelName.trim().toLowerCase())) {
            setError('A level with this name already exists.');
            return;
        }
        onAdd({ levelName: levelName.trim(), targetUM: numericUM, unit });
        handleClose();
    };

    return (
        <CustomModal
            visible={isOpen}
            onDismiss={handleClose}
            header="Add Service Level"
            footer={
                <div className="modal-footer-actions">
                    <Btn variant="secondary-outlined" buttonText="Cancel" buttonOnClick={handleClose} />
                    <Btn variant="primary" buttonText="Add Level" buttonOnClick={handleAdd} />
                </div>
            }
        >
            {error && (
                <p className="pm-modal-error" role="alert">
                    {error}
                </p>
            )}

            <div className="pm-modal-fields">
                <label className="pm-modal-select-field">
                    <span className="pm-modal-select-label">Level name</span>
                    <SearchSuggest
                        options={knownLevelNames.map((name) => ({ value: name, label: name }))}
                        value={levelName}
                        onChange={setLevelName}
                        placeholder="Choose a name or add one"
                        allowNew
                        newLabel="+ New level name"
                        ariaLabel="Level name"
                        inputId="pm-add-level-name"
                    />
                </label>

                <div className="pm-modal-row">
                    <InputField
                        label="Target UM"
                        type="number"
                        min={1}
                        value={targetUM}
                        onChange={setTargetUM}
                        placeholder="4000"
                        ariaLabel="Target unit of measure"
                    />
                    <label className="pm-modal-select-field">
                        <span className="pm-modal-select-label">Unit</span>
                        <Select
                            options={unitOptions}
                            value={unit}
                            onChange={setUnit}
                            ariaLabel="Unit of measure"
                        />
                    </label>
                </div>
            </div>

            <p className="pm-modal-hint">
                Adds an empty column to the matrix. UM unit defaults to this equipment’s tracking type (
                <strong>{displayUnitOfMeasure(defaultUnit)}</strong>).
            </p>
        </CustomModal>
    );
};
